/**
 * Edit Game Dialog for Master Database
 * - Full game editing form with all fields
 * - Platform ID lookups (Xbox, Steam, PSN)
 * - Challenge eligibility settings
 * - System bar configuration
 */
import { useState, useEffect, useRef } from "react";
import { Loader2, Save, Search, X, Eye, Tag, Gamepad2, Target, ExternalLink, Plus, Building2, Wrench, Shield, AlertTriangle, FileText, Link2, Trophy, ArrowDownToLine, Ban, Layers } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import ImageUpload from "@/components/ImageUpload";
import SystemBarOverlay, { getSystemBarOptions } from "@/components/SystemBarOverlay";
import { buildTagSections } from "./groupedTags";
import GameLinksEditor from "./GameLinksEditor";
import CrossPlayLinksDialog from "@/components/MasterDatabase/CrossPlayLinksDialog";
import VersionLinksDialog from "@/components/MasterDatabase/VersionLinksDialog";
import { toast } from "sonner";
import axios from "@/lib/api";

import { getApiUrl } from "@/lib/api";
const METADATA_API = `${getApiUrl()}/api/metadata-tags`;

// Inline multi-select with autocomplete and create-new for publishers/developers/ratings
function MetadataMultiSelect({ label, icon: Icon, iconColor, metaType, selected, onChange, placeholder }) {
  const [options, setOptions] = useState([]);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    axios.get(`${METADATA_API}/${metaType}`).then(res => setOptions(res.data || [])).catch(() => {});
  }, [metaType]);

  const filtered = options.filter(o => o.name.toLowerCase().includes(query.toLowerCase()) && !selected.includes(o.name));
  const exactMatch = options.some(o => o.name.toLowerCase() === query.toLowerCase());

  const addItem = (name) => {
    if (!selected.includes(name)) {
      onChange([...selected, name]);
    }
    setQuery("");
    setOpen(false);
  };

  const removeItem = (name) => onChange(selected.filter(n => n !== name));

  const createAndAdd = async () => {
    if (!query.trim()) return;
    try {
      const res = await axios.post(`${METADATA_API}/${metaType}`, { name: query.trim() });
      setOptions(prev => [...prev, res.data]);
      addItem(res.data.name);
      toast.success(`Created "${res.data.name}"`);
    } catch (err) {
      if (err.response?.status === 409) {
        addItem(query.trim());
      } else {
        toast.error(err.response?.data?.detail || "Failed to create");
      }
    }
  };

  return (
    <div className="space-y-2">
      <Label className="flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4" style={{ color: iconColor }} />}
        {label}
      </Label>
      {/* Selected items */}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map(name => (
            <span key={name} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-white/10 text-white border border-white/10">
              {name}
              <button onClick={() => removeItem(name)} className="hover:text-red-400"><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
      )}
      {/* Input with dropdown */}
      <div className="relative">
        <Input
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          className="bg-black/50 border-white/10"
          data-testid={`${metaType}-input`}
        />
        {open && query.length > 0 && (
          <div className="absolute z-50 w-full mt-1 max-h-40 overflow-y-auto bg-[#0A0A0A] border border-white/10 rounded-lg shadow-lg">
            {filtered.slice(0, 8).map(o => (
              <button key={o.id} onClick={() => addItem(o.name)} className="w-full text-left px-3 py-2 text-sm text-white hover:bg-white/10 truncate" data-testid={`${metaType}-option-${o.id}`}>
                {o.name}
                {o.system && <span className="text-white/30 ml-2 text-xs">({o.system})</span>}
              </button>
            ))}
            {!exactMatch && query.trim() && (
              <button onClick={createAndAdd} className="w-full text-left px-3 py-2 text-sm text-teal-400 hover:bg-teal-500/10 flex items-center gap-2 border-t border-white/5" data-testid={`${metaType}-create-new`}>
                <Plus className="w-3 h-3" /> Create "{query.trim()}"
              </button>
            )}
            {filtered.length === 0 && exactMatch && (
              <div className="px-3 py-2 text-sm text-white/30">Already selected or no more matches</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Helper to format YYYY-MM-DD to MM/DD/YYYY for display
const formatDateForDisplay = (dateStr) => {
  if (!dateStr) return "";
  const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) {
    return `${match[2]}/${match[3]}/${match[1]}`;
  }
  return dateStr;
};

// Helper for auto-masking date input (MM/DD/YYYY)
const handleDateInputMask = (inputValue) => {
  let digits = inputValue.replace(/\D/g, "");
  digits = digits.slice(0, 8);
  
  let formatted = "";
  if (digits.length > 0) {
    formatted = digits.slice(0, 2);
  }
  if (digits.length > 2) {
    formatted += "/" + digits.slice(2, 4);
  }
  if (digits.length > 4) {
    formatted += "/" + digits.slice(4, 8);
  }
  
  return formatted;
};

// Region options (fallback when no admin-managed list is provided via props)
const DEFAULT_REGION_OPTIONS = [
  { value: "none", label: "No Region" },
  { value: "WW", label: "Worldwide (WW)" },
  { value: "NA", label: "North America (NA)" },
  { value: "EU", label: "Europe (EU)" },
  { value: "JP", label: "Japan (JP)" },
  { value: "AS", label: "Asia (AS)" },
  { value: "AU", label: "Australia (AU)" },
  { value: "KR", label: "Korea (KR)" },
  { value: "CN", label: "China (CN)" },
];

/**
 * Edit Game Dialog Component
 */
export function EditGameDialog({
  open,
  onOpenChange,
  editingGame,
  onEditingGameChange,
  onSave,
  // Config data
  platforms,
  genres,
  unifiedTags,
  contentTags,
  systemOptions,
  unifiedTagGroups = [],
  // Selected game for achievements list
  selectedGame,
  // Platform lookup handlers
  onXboxLookup,
  xboxLookupLoading,
  onSteamLookup,
  steamLookupLoading,
  onPsnLookup,
  psnLookupLoading,
  // Genre remap handler
  onRemapGenre,
  // Admin-managed region options (falls back to built-in list)
  regionOptions = DEFAULT_REGION_OPTIONS,
}) {
  const [crossPlayDialogOpen, setCrossPlayDialogOpen] = useState(false);
  const [versionDialogOpen, setVersionDialogOpen] = useState(false);
  const scrollBodyRef = useRef(null);
  const publishSectionRef = useRef(null);
  if (!editingGame) return null;

  // Case-insensitive membership check that treats stored variants like
  // 'PlayStation' (display name) and 'playstation' (slug) as equivalent.
  // Fixes a legacy bug where games saved before normalization stored the
  // display name — checkboxes appeared unchecked and re-checking produced
  // a duplicate entry in the platforms array on save.
  const matchesTag = (list, ...variants) => {
    if (!Array.isArray(list) || !list.length) return false;
    const set = new Set(list.filter(Boolean).map((v) => String(v).toLowerCase()));
    return variants.some((v) => v && set.has(String(v).toLowerCase()));
  };
  const removeTag = (list, ...variants) => {
    const lows = new Set(variants.filter(Boolean).map((v) => String(v).toLowerCase()));
    return (list || []).filter((v) => !lows.has(String(v).toLowerCase()));
  };
  const addTagOnce = (list, canonical) => {
    const canonLow = String(canonical).toLowerCase();
    const filtered = (list || []).filter((v) => String(v).toLowerCase() !== canonLow);
    return [...filtered, canonical];
  };

  const handleDateChange = (e) => {
    const formatted = handleDateInputMask(e.target.value);
    if (formatted.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
      const [month, day, year] = formatted.split('/');
      onEditingGameChange({...editingGame, releaseDate: `${year}-${month}-${day}`, _dateDisplay: formatted});
    } else {
      onEditingGameChange({...editingGame, releaseDate: "", _dateDisplay: formatted});
    }
  };

  const handleClearAllTags = () => {
    onEditingGameChange({
      ...editingGame,
      platforms: [],
      systems: [],
      contentTags: []
    });
    toast.info("Tags cleared - click Save to apply");
  };

  const jumpToPublish = () => {
    const target = publishSectionRef.current;
    const container = scrollBodyRef.current;
    if (target && container) {
      container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
      // Brief highlight so the admin sees where they landed
      target.classList.add("ring-2", "ring-green-400/60", "rounded-lg");
      setTimeout(() => target.classList.remove("ring-2", "ring-green-400/60", "rounded-lg"), 1500);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>{editingGame?.id ? "Edit Game" : "Add Game"}</DialogTitle>
          {/* Jump to the Published/Visibility checkboxes at the bottom. Sits
              just left of the built-in close (X) button. */}
          <button
            type="button"
            onClick={jumpToPublish}
            title="Jump to Publish"
            data-testid="jump-to-publish-btn"
            className="absolute right-11 top-4 flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-green-400 bg-green-500/10 hover:bg-green-500/20 transition-colors"
          >
            <ArrowDownToLine className="w-3.5 h-3.5" />
            Publish
          </button>
        </DialogHeader>
        
        <div ref={scrollBodyRef} className="space-y-4 pt-4 overflow-y-auto flex-1 pr-2 -mr-2">
          {/* Game Name */}
          <div className="space-y-2">
            <Label>Game Name *</Label>
            <Input
              value={editingGame.name}
              onChange={(e) => onEditingGameChange({...editingGame, name: e.target.value})}
              className="bg-black/50 border-white/10"
            />
          </div>
          
          {/* Publishers */}
          <MetadataMultiSelect
            label="Publishers"
            icon={Building2}
            iconColor="#F97316"
            metaType="publishers"
            selected={editingGame.publishers || []}
            onChange={(v) => onEditingGameChange({...editingGame, publishers: v})}
            placeholder="Search publishers..."
          />

          {/* Developers */}
          <MetadataMultiSelect
            label="Developers"
            icon={Wrench}
            iconColor="#06B6D4"
            metaType="developers"
            selected={editingGame.developers || []}
            onChange={(v) => onEditingGameChange({...editingGame, developers: v})}
            placeholder="Search developers..."
          />

          {/* Game Ratings */}
          <MetadataMultiSelect
            label="Ratings"
            icon={Shield}
            iconColor="#F43F5E"
            metaType="ratings"
            selected={editingGame.ratings || []}
            onChange={(v) => onEditingGameChange({...editingGame, ratings: v})}
            placeholder="Search ratings (ESRB, PEGI...)"
          />
          
          {/* Release Date & Completion Estimates */}
          <div className="grid grid-cols-1 gap-4">
            <div className="space-y-2">
              <Label>Release Date</Label>
              <Input
                type="text"
                value={editingGame._dateDisplay !== undefined ? editingGame._dateDisplay : (editingGame.releaseDate ? formatDateForDisplay(editingGame.releaseDate) : "")}
                onChange={handleDateChange}
                placeholder="MM/DD/YYYY"
                className="bg-black/50 border-white/10"
                maxLength={10}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Fast Completion</Label>
                <Input
                  value={editingGame.completionEstimateFast || ""}
                  onChange={(e) => onEditingGameChange({...editingGame, completionEstimateFast: e.target.value})}
                  placeholder="e.g., 10 hours"
                  className="bg-black/50 border-white/10"
                  data-testid="completion-fast-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Full Completion</Label>
                <Input
                  value={editingGame.completionEstimateLong || ""}
                  onChange={(e) => onEditingGameChange({...editingGame, completionEstimateLong: e.target.value})}
                  placeholder="e.g., 50 hours"
                  className="bg-black/50 border-white/10"
                  data-testid="completion-long-input"
                />
              </div>
            </div>
          </div>
          
          {/* Box Art */}
          <div className="space-y-2">
            <Label>Box Art</Label>
            <div className="flex items-start gap-4">
              <div className="flex flex-col rounded overflow-hidden bg-black/50">
                {editingGame.showSystemBar && editingGame.boxArtUrl && (
                  <SystemBarOverlay 
                    platform={editingGame.platforms?.[0]} 
                    system={editingGame.systems?.[0]}
                    override={editingGame.systemBarOverride}
                    show={true}
                  />
                )}
                <ImageUpload
                  folder="gamepop/games"
                  currentImageUrl={editingGame.boxArtUrl || ""}
                  onImageUploaded={(url) => onEditingGameChange({...editingGame, boxArtUrl: url})}
                  size="md"
                />
              </div>
              <div className="flex-1 space-y-2">
                <Input
                  value={editingGame.boxArtUrl || ""}
                  onChange={(e) => onEditingGameChange({...editingGame, boxArtUrl: e.target.value})}
                  placeholder="Or paste URL here..."
                  className="bg-black/50 border-white/10"
                />
                <p className="text-xs text-white/40">Upload an image or paste a URL</p>
              </div>
            </div>
            
            {/* System Bar Overlay Options */}
            <div className="mt-3 p-3 bg-white/5 border border-white/10 rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Label htmlFor="show-system-bar" className="text-sm cursor-pointer">
                    Show System Bar
                  </Label>
                  <span className="text-xs text-white/40">(overlay on cover art)</span>
                </div>
                <Switch
                  id="show-system-bar"
                  checked={editingGame.showSystemBar || false}
                  onCheckedChange={(checked) => onEditingGameChange({...editingGame, showSystemBar: checked})}
                  data-testid="show-system-bar-toggle"
                />
              </div>
              
              {editingGame.showSystemBar && (
                <div className="space-y-2">
                  <Label className="text-xs text-white/60">System Bar Style</Label>
                  <Select 
                    value={editingGame.systemBarOverride || "auto"} 
                    onValueChange={(v) => onEditingGameChange({...editingGame, systemBarOverride: v === "auto" ? null : v})}
                  >
                    <SelectTrigger className="bg-black/50 border-white/10 h-8 text-sm">
                      <SelectValue placeholder="Auto-detect from system" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10 max-h-60">
                      <SelectItem value="auto">
                        <span className="flex items-center gap-2">
                          <span className="text-white/60">Auto-detect</span>
                        </span>
                      </SelectItem>
                      {getSystemBarOptions().map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          <span className="flex items-center gap-2">
                            <span 
                              className="w-3 h-3 rounded" 
                              style={{ backgroundColor: option.color }}
                            />
                            {option.label || option.value}
                            {option.hasBanner && (
                              <span className="text-[10px] text-green-400 ml-1">(image)</span>
                            )}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-white/40">
                    {editingGame.systemBarOverride 
                      ? `Manually set to: ${editingGame.systemBarOverride}`
                      : `Auto-detected: ${editingGame.systems?.[0] || editingGame.platforms?.[0] || 'None'}`
                    }
                  </p>
                </div>
              )}
            </div>
          </div>
          
          {/* Challenge Eligibility Section */}
          <div className="space-y-3 p-3 bg-[#121212] rounded-lg border border-white/10">
            <Label className="flex items-center gap-2">
              <Target className="w-4 h-4 text-blue-400" />
              Challenge Eligibility
            </Label>
            <Select
              value={editingGame.challengeEligibility || "eligible"}
              onValueChange={(value) => onEditingGameChange({
                ...editingGame, 
                challengeEligibility: value,
                challengeExcludedAchievements: value === "partly-eligible" ? (editingGame.challengeExcludedAchievements || []) : []
              })}
            >
              <SelectTrigger className="bg-[#0A0A0A] border-white/10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-white/10">
                <SelectItem value="eligible">
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-green-500" />
                    Eligible for Challenges
                  </span>
                </SelectItem>
                <SelectItem value="partly-eligible">
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-yellow-500" />
                    Partly Eligible (Some Excluded)
                  </span>
                </SelectItem>
                <SelectItem value="not-eligible">
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-red-500" />
                    Not Eligible for Challenges
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
            
            {editingGame.challengeEligibility === "partly-eligible" && (
              <div className="space-y-2">
                <Label className="text-xs text-white/60">Excluded Achievements</Label>
                <p className="text-xs text-white/40">
                  Select achievements that cannot be used in challenges (e.g., unobtainable, glitched)
                </p>
                {selectedGame?.achievements?.length > 0 ? (
                  <div className="max-h-48 overflow-y-auto space-y-1 bg-[#0A0A0A] rounded p-2">
                    {selectedGame.achievements.map((ach) => (
                      <label 
                        key={ach.id}
                        className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-white/5 ${
                          (editingGame.challengeExcludedAchievements || []).includes(ach.id) 
                            ? "bg-red-500/10 border border-red-500/30" 
                            : ""
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={(editingGame.challengeExcludedAchievements || []).includes(ach.id)}
                          onChange={(e) => {
                            const excluded = editingGame.challengeExcludedAchievements || [];
                            if (e.target.checked) {
                              onEditingGameChange({
                                ...editingGame,
                                challengeExcludedAchievements: [...excluded, ach.id]
                              });
                            } else {
                              onEditingGameChange({
                                ...editingGame,
                                challengeExcludedAchievements: excluded.filter(id => id !== ach.id)
                              });
                            }
                          }}
                          className="rounded border-white/20"
                        />
                        {ach.imageUrl && (
                          <img src={ach.imageUrl} alt="" className="w-6 h-6 rounded" />
                        )}
                        <span className="text-sm text-white/80 truncate">{ach.name}</span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-white/40 italic">
                    Select a game first to see its achievements
                  </p>
                )}
                {(editingGame.challengeExcludedAchievements || []).length > 0 && (
                  <p className="text-xs text-red-400">
                    {editingGame.challengeExcludedAchievements.length} achievement(s) excluded
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Genres */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Genres</Label>
              {editingGame.genres?.length > 0 && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => onEditingGameChange({...editingGame, genres: []})}
                  className="h-6 text-[10px] text-red-400 hover:text-red-300 px-2"
                >
                  Clear All
                </Button>
              )}
            </div>
            {/* Show unrecognized genres with remap/remove options */}
            {editingGame.genres?.some(g => !genres.find(mg => mg.slug === g)) && (
              <div className="text-xs text-yellow-400 bg-yellow-500/10 border border-yellow-500/30 rounded p-2 mb-2">
                <p className="font-medium mb-2">Unrecognized genres:</p>
                <div className="flex flex-wrap gap-2">
                  {editingGame.genres.filter(g => !genres.find(mg => mg.slug === g)).map((g, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center gap-1 px-2 py-1 rounded bg-yellow-500/20"
                    >
                      <span>{g}</span>
                      <button
                        onClick={() => onRemapGenre(g)}
                        className="ml-1 px-1.5 py-0.5 rounded bg-blue-500/30 hover:bg-blue-500/50 text-blue-300 text-[10px]"
                        title="Remap to recognized genre"
                      >
                        Remap
                      </button>
                      <button
                        onClick={() => onEditingGameChange({...editingGame, genres: editingGame.genres.filter(x => x !== g)})}
                        className="px-1 py-0.5 rounded hover:bg-red-500/30 text-red-300"
                        title="Remove genre"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2 p-2 border border-white/10 rounded-lg bg-black/50">
              {[...genres].sort((a, b) => a.name.localeCompare(b.name)).map((genre) => (
                <label 
                  key={genre.id} 
                  className={`flex items-center gap-2 px-2 py-1 rounded cursor-pointer transition-colors ${
                    (editingGame.genres || []).includes(genre.slug) 
                      ? 'bg-purple-500/30 border border-purple-500/50' 
                      : 'bg-white/5 hover:bg-white/10'
                  }`}
                >
                  <Checkbox
                    checked={(editingGame.genres || []).includes(genre.slug)}
                    onCheckedChange={(checked) => {
                      const currentGenres = editingGame.genres || [];
                      if (checked) {
                        onEditingGameChange({...editingGame, genres: [...currentGenres, genre.slug]});
                      } else {
                        onEditingGameChange({...editingGame, genres: currentGenres.filter(g => g !== genre.slug)});
                      }
                    }}
                    className="border-white/30"
                  />
                  <div 
                    className="w-2 h-2 rounded-full" 
                    style={{ backgroundColor: genre.color }}
                  />
                  <span className="text-xs text-white">{genre.name}</span>
                </label>
              ))}
            </div>
          </div>
          
          {/* Platforms & Region */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Platforms</Label>
              <div className="flex flex-wrap gap-2">
                {platforms.map((p) => (
                  <label key={p.slug} className="flex items-center gap-2 px-2 py-1 rounded bg-white/5 cursor-pointer hover:bg-white/10">
                    <Checkbox
                      checked={matchesTag(editingGame.platforms, p.slug, p.name)}
                      onCheckedChange={(checked) => {
                        const currentPlatforms = editingGame.platforms || [];
                        const systems = editingGame.systems || [];
                        const currentTags = editingGame.contentTags || [];
                        if (checked) {
                          onEditingGameChange({...editingGame, platforms: addTagOnce(currentPlatforms, p.slug)});
                        } else {
                          const platformSystems = systemOptions[p.slug]?.map(s => s.value) || [];
                          const platformSystemsLow = new Set(platformSystems.map(s => String(s).toLowerCase()));
                          onEditingGameChange({
                            ...editingGame, 
                            platforms: removeTag(currentPlatforms, p.slug, p.name),
                            systems: systems.filter(s => !platformSystemsLow.has(String(s).toLowerCase())),
                            contentTags: currentTags.filter(tagId => {
                              const tag = contentTags.find(t => t.id === tagId);
                              if (!tag) return false;
                              const tagSystem = platforms.flatMap(pl => pl.systems || []).find(s => s.id === tag.systemId);
                              return tagSystem && !platformSystemsLow.has(String(tagSystem.slug).toLowerCase());
                            })
                          });
                        }
                      }}
                    />
                    <span className="text-xs">{p.name}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Region</Label>
              <Select value={editingGame.region || "none"} onValueChange={(v) => onEditingGameChange({...editingGame, region: v})}>
                <SelectTrigger className="bg-black/50 border-white/10">
                  <SelectValue placeholder="Select region" />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/10">
                  {regionOptions.map((r) => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Systems based on selected platforms */}
          {editingGame.platforms?.length > 0 && (
            <div className="space-y-2">
              <Label>Systems <span className="text-white/40 text-xs font-normal">(playable on)</span></Label>
              <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto p-2 bg-black/30 rounded border border-white/10">
                {editingGame.platforms.flatMap(platform => {
                  // Resolve system options tolerantly — legacy games may store
                  // the platform display name ('PlayStation') instead of slug
                  // ('playstation'), so try both.
                  const opts = systemOptions[platform]
                    || systemOptions[String(platform).toLowerCase()]
                    || (platforms.find(p => (p.slug || "").toLowerCase() === String(platform).toLowerCase() || (p.name || "").toLowerCase() === String(platform).toLowerCase())
                        ? systemOptions[platforms.find(p => (p.slug || "").toLowerCase() === String(platform).toLowerCase() || (p.name || "").toLowerCase() === String(platform).toLowerCase()).slug]
                        : [])
                    || [];
                  return opts.map(sys => (
                    <label key={`${platform}-${sys.value}`} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={matchesTag(editingGame.systems, sys.value, sys.label)}
                        onCheckedChange={(checked) => {
                          const systems = editingGame.systems || [];
                          if (checked) {
                            const nextSystems = addTagOnce(systems, sys.value);
                            const updates = { ...editingGame, systems: nextSystems };
                            // Auto-default primarySystem to the first checked system when nothing was set yet.
                            if (!editingGame.primarySystem) updates.primarySystem = sys.value;
                            onEditingGameChange(updates);
                          } else {
                            const nextSystems = removeTag(systems, sys.value, sys.label);
                            const updates = { ...editingGame, systems: nextSystems };
                            // If we just unchecked the current primary, reassign to the next checked system (or clear).
                            if (String(editingGame.primarySystem || "").toLowerCase() === String(sys.value).toLowerCase() ||
                                String(editingGame.primarySystem || "").toLowerCase() === String(sys.label || "").toLowerCase()) {
                              updates.primarySystem = nextSystems[0] || null;
                            }
                            onEditingGameChange(updates);
                          }
                        }}
                      />
                      <span className="text-xs">{sys.label}</span>
                    </label>
                  ));
                })}
              </div>

              {/* Primary System — the single system shown on Library / Browse tiles.
                  Only the systems currently checked above are eligible. The full
                  list of checked systems is shown in detailed surfaces (GameDetail
                  "Playable on:" + Library hover tooltip) with this primary first. */}
              {editingGame.systems?.length > 0 && (
                <div className="flex items-center gap-2 pt-1" data-testid="primary-system-row">
                  <Label className="text-xs font-normal text-white/60 whitespace-nowrap">Primary System</Label>
                  <Select
                    value={(() => {
                      // Resolve stored primarySystem to a canonical option value
                      // so legacy display-name variants still match a SelectItem.
                      const raw = editingGame.primarySystem || editingGame.systems[0] || "";
                      const rawLow = String(raw).toLowerCase();
                      for (const platform of editingGame.platforms) {
                        const opts = systemOptions[platform]
                          || systemOptions[String(platform).toLowerCase()]
                          || (() => {
                              const p = platforms.find(pl => (pl.slug || "").toLowerCase() === String(platform).toLowerCase() || (pl.name || "").toLowerCase() === String(platform).toLowerCase());
                              return p ? (systemOptions[p.slug] || []) : [];
                            })();
                        for (const sys of opts) {
                          if (String(sys.value).toLowerCase() === rawLow || String(sys.label || "").toLowerCase() === rawLow) {
                            return sys.value;
                          }
                        }
                      }
                      return raw;
                    })()}
                    onValueChange={(val) => onEditingGameChange({ ...editingGame, primarySystem: val })}
                  >
                    <SelectTrigger
                      className="h-8 bg-white/5 border-white/10 text-xs flex-1"
                      data-testid="primary-system-select"
                    >
                      <SelectValue placeholder="Pick the primary system" />
                    </SelectTrigger>
                    <SelectContent>
                      {editingGame.platforms.flatMap(platform => {
                        const opts = systemOptions[platform]
                          || systemOptions[String(platform).toLowerCase()]
                          || (() => {
                              const p = platforms.find(pl => (pl.slug || "").toLowerCase() === String(platform).toLowerCase() || (pl.name || "").toLowerCase() === String(platform).toLowerCase());
                              return p ? (systemOptions[p.slug] || []) : [];
                            })();
                        return opts
                          .filter(sys => matchesTag(editingGame.systems, sys.value, sys.label))
                          .map(sys => (
                            <SelectItem key={`${platform}-${sys.value}`} value={sys.value} data-testid={`primary-system-opt-${sys.value}`}>
                              {sys.label}
                            </SelectItem>
                          ));
                      })}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}
          
          {/* Availability */}
          <div className="space-y-2">
            <Label>Availability</Label>
            <div className="flex gap-4 items-center">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={editingGame.availableDigital}
                  onCheckedChange={(checked) => onEditingGameChange({...editingGame, availableDigital: checked})}
                />
                <span className="text-sm">Digital</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={editingGame.availablePhysical}
                  onCheckedChange={(checked) => onEditingGameChange({...editingGame, availablePhysical: checked})}
                />
                <span className="text-sm">Physical</span>
              </label>
            </div>
          </div>

          {/* Multiplayer */}
          <div className="space-y-2">
            <Label>Multiplayer</Label>
            <div className="flex gap-4 items-center flex-wrap">
              <div className="flex items-center gap-2">
                <span className="text-sm text-white/60">Type:</span>
                <Select
                  value={editingGame.multiplayerType || (editingGame.hasMultiplayer ? "full" : "none")}
                  onValueChange={(val) => onEditingGameChange({...editingGame, multiplayerType: val})}
                >
                  <SelectTrigger className="w-[180px] h-8 bg-white/5 border-white/10 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="full">Full Multiplayer</SelectItem>
                    <SelectItem value="local_only">Local Only</SelectItem>
                    <SelectItem value="limited">Limited</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <label className="flex items-center gap-2 cursor-pointer" title="Cross-platform play merges billboards & players across all platforms">
                <Checkbox
                  checked={!!editingGame.crossPlatform}
                  onCheckedChange={(checked) => onEditingGameChange({...editingGame, crossPlatform: !!checked})}
                  data-testid="mdb-crossplatform-checkbox"
                />
                <span className="text-sm">Cross-Platform Play</span>
              </label>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={!editingGame.crossPlatform || !editingGame.id}
                onClick={() => setCrossPlayDialogOpen(true)}
                data-testid="mdb-edit-crossplay-links"
                className="bg-white/5 border-white/10 text-white/70 hover:text-white hover:bg-white/10 h-8 disabled:opacity-30"
                title={!editingGame.id ? "Save the game first" : "Pick the matching cross-play records"}
              >
                <Link2 className="w-3.5 h-3.5 mr-1.5" />
                Edit Links{Array.isArray(editingGame.crossPlatformLinks) && editingGame.crossPlatformLinks.length > 0 ? ` (${editingGame.crossPlatformLinks.length})` : ""}
              </Button>
            </div>
            {/* Match Types (only relevant when this game supports multiplayer) */}
            {(editingGame.multiplayerType && editingGame.multiplayerType !== "none") || editingGame.hasMultiplayer ? (
              <div className="pt-2">
                <p className="text-xs text-white/50 mb-1.5">Match Types supported (leave empty for "no constraint"):</p>
                <div className="flex flex-wrap gap-3">
                  {[
                    { key: "coop", label: "Co-op" },
                    { key: "versus", label: "Versus" },
                    { key: "ranked", label: "Ranked" },
                    { key: "social", label: "Casual" },
                    { key: "custom", label: "Custom" },
                  ].map(({ key, label }) => {
                    const list = Array.isArray(editingGame.matchTypes) ? editingGame.matchTypes : [];
                    const checked = list.includes(key);
                    return (
                      <label key={key} className="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                          data-testid={`mdb-matchtype-${key}`}
                          checked={checked}
                          onCheckedChange={(c) => {
                            const next = c ? [...list, key] : list.filter(k => k !== key);
                            onEditingGameChange({ ...editingGame, matchTypes: Array.from(new Set(next)) });
                          }}
                        />
                        <span className="text-sm">{label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            ) : null}
          </div>

          {/* Delisted / Unavailable */}
          <div className="space-y-3 p-3 bg-amber-500/5 border border-amber-500/20 rounded-lg">
            <Label className="flex items-center gap-2 text-amber-400 text-sm font-medium">
              <AlertTriangle className="w-4 h-4" />
              Delisted / Unavailable
            </Label>
            <Select
              value={editingGame.delistedStatus || "none"}
              onValueChange={(v) => onEditingGameChange({
                ...editingGame,
                delistedStatus: v,
                delistedReason: v === "none" ? "" : (editingGame.delistedReason || "")
              })}
            >
              <SelectTrigger className="bg-black/50 border-white/10" data-testid="delisted-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A0A] border-white/10">
                <SelectItem value="none">
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-green-500" />
                    Available
                  </span>
                </SelectItem>
                <SelectItem value="delisted">
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-amber-500" />
                    Delisted
                  </span>
                </SelectItem>
                <SelectItem value="servers-shutdown">
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-red-500" />
                    Servers Shut Down
                  </span>
                </SelectItem>
                <SelectItem value="removed-digital">
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-orange-500" />
                    Removed from Digital Stores
                  </span>
                </SelectItem>
                <SelectItem value="no-longer-playable">
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-red-600" />
                    No Longer Playable
                  </span>
                </SelectItem>
                <SelectItem value="other">
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-gray-500" />
                    Other
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
            {editingGame.delistedStatus && editingGame.delistedStatus !== "none" && (
              <div className="space-y-1">
                <Label className="text-xs text-white/50">Details (optional)</Label>
                <Input
                  value={editingGame.delistedReason || ""}
                  onChange={(e) => onEditingGameChange({...editingGame, delistedReason: e.target.value})}
                  placeholder="e.g., Removed from Xbox Store on Jan 2024..."
                  className="bg-black/50 border-white/10"
                  data-testid="delisted-reason-input"
                />
                <p className="text-xs text-amber-400/60">This will hide buy links and show a notice on the Browse page.</p>
              </div>
            )}
          </div>
          
          {/* Content Tags - Unified */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Tag className="w-4 h-4 text-teal-400" />
              Content Tags
            </Label>
            <div className="flex flex-col gap-3 p-2 bg-black/30 rounded border border-white/10 min-h-[40px] max-h-60 overflow-y-auto">
              {unifiedTags.length === 0 ? (
                <span className="text-white/40 text-xs italic">
                  No unified tags available. Create them in Manage Tags.
                </span>
              ) : (
                buildTagSections(unifiedTags, unifiedTagGroups).map(section => (
                  <div key={section.id} data-testid={`game-tag-group-${section.id}`}>
                    {unifiedTagGroups.length > 0 && (
                      <p className="text-[10px] font-semibold text-white/40 uppercase tracking-wider mb-1 px-1">
                        {section.name}
                      </p>
                    )}
                    <div className="flex flex-wrap gap-2">
                      {section.tags.map(tag => (
                        <label
                          key={tag.id}
                          className="flex items-center gap-2 px-2 py-1 rounded cursor-pointer hover:bg-white/10 transition-colors"
                          style={{ backgroundColor: tag.color + "15" }}
                        >
                          <Checkbox
                            checked={editingGame.contentTags?.includes(tag.id)}
                            onCheckedChange={(checked) => {
                              const currentTags = editingGame.contentTags || [];
                              if (checked) {
                                onEditingGameChange({...editingGame, contentTags: [...currentTags, tag.id]});
                              } else {
                                onEditingGameChange({...editingGame, contentTags: currentTags.filter(x => x !== tag.id)});
                              }
                            }}
                          />
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: tag.color }} />
                          <span className="text-xs" style={{ color: tag.color }}>
                            {tag.name}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
            {editingGame.contentTags?.length > 0 && (
              <p className="text-xs text-teal-400">
                {editingGame.contentTags.length} tag(s) selected
              </p>
            )}
          </div>
          
          {/* Xbox Title ID */}
          {editingGame.platforms?.some(p => p.toLowerCase() === "xbox") && (
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Gamepad2 className="w-4 h-4 text-green-400" />
                Xbox Title ID
              </Label>
              <div className="flex gap-2">
                <Input
                  value={editingGame.xboxTitleId || ""}
                  onChange={(e) => onEditingGameChange({...editingGame, xboxTitleId: e.target.value})}
                  placeholder="e.g., 1480657291 or 5841098B"
                  className="bg-black/50 border-white/10"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onXboxLookup('edit')}
                  disabled={xboxLookupLoading || !editingGame.name}
                  className="border-green-500/30 hover:bg-green-500/10 text-green-400 whitespace-nowrap"
                >
                  {xboxLookupLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Search className="w-4 h-4 mr-1" />
                      Lookup
                    </>
                  )}
                </Button>
              </div>
              <p className="text-xs text-white/40">Decimal or Hex format (e.g., 1480657291 or 5841098B)</p>
            </div>
          )}
          
          {/* Xbox Bing ID */}
          {editingGame.platforms?.some(p => p.toLowerCase() === "xbox") && (
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Gamepad2 className="w-4 h-4 text-green-400" />
                Xbox Bing ID (Xbox 360)
              </Label>
              <Input
                value={editingGame.xboxBingId || ""}
                onChange={(e) => onEditingGameChange({...editingGame, xboxBingId: e.target.value})}
                placeholder="e.g., 66acd000-77fe-1000-9115-d8025841098b"
                className="bg-black/50 border-white/10"
              />
              <p className="text-xs text-white/40">GUID format for Xbox 360 games (from dbox.tools)</p>
            </div>
          )}
          
          {/* Steam App ID */}
          {editingGame.platforms?.some(p => p.toLowerCase() === "steam") && (
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Gamepad2 className="w-4 h-4 text-blue-400" />
                Steam App ID
              </Label>
              <div className="flex gap-2">
                <Input
                  value={editingGame.steamAppId || ""}
                  onChange={(e) => onEditingGameChange({...editingGame, steamAppId: e.target.value})}
                  placeholder="e.g., 730"
                  className="bg-black/50 border-white/10"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onSteamLookup('edit')}
                  disabled={steamLookupLoading || !editingGame.name}
                  className="border-blue-500/30 hover:bg-blue-500/10 text-blue-400 whitespace-nowrap"
                >
                  {steamLookupLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Search className="w-4 h-4 mr-1" />
                      Lookup
                    </>
                  )}
                </Button>
              </div>
              <p className="text-xs text-white/40">Required for Steam achievement sync. Click Lookup to search Steam.</p>
            </div>
          )}
          
          {/* PlayStation NP ID */}
          {editingGame.platforms?.some(p => p.toLowerCase() === "playstation") && (
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Gamepad2 className="w-4 h-4 text-blue-500" />
                PlayStation NP ID
              </Label>
              <div className="flex gap-2">
                <Input
                  value={editingGame.npCommunicationId || ""}
                  onChange={(e) => onEditingGameChange({...editingGame, npCommunicationId: e.target.value})}
                  placeholder="e.g., NPWR12345_00"
                  className="bg-black/50 border-white/10"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onPsnLookup('edit')}
                  disabled={psnLookupLoading || !editingGame.name}
                  className="border-blue-600/30 hover:bg-blue-600/10 text-blue-500 whitespace-nowrap"
                >
                  {psnLookupLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Search className="w-4 h-4 mr-1" />
                      Lookup
                    </>
                  )}
                </Button>
              </div>
              <p className="text-xs text-white/40">Required for PlayStation trophy sync. Click Lookup to search your trophy list.</p>
            </div>
          )}
          
          {/* Exophase Overrides */}
          <div className="space-y-3 p-3 bg-orange-500/5 border border-orange-500/20 rounded-lg">
            <Label className="flex items-center gap-2 text-orange-400 text-sm font-medium">
              <ExternalLink className="w-4 h-4" />
              Exophase Verification
            </Label>
            <div className="space-y-2">
              <div>
                <Label className="text-xs text-white/50 mb-1 block">Exophase Game Name</Label>
                <Input
                  value={editingGame.exophaseName || ""}
                  onChange={(e) => onEditingGameChange({...editingGame, exophaseName: e.target.value})}
                  placeholder="How the game is listed on Exophase..."
                  className="bg-black/50 border-white/10"
                  data-testid="exophase-name-input"
                />
                <p className="text-xs text-white/30 mt-0.5">Override the search name when Exophase uses a different/abbreviated title</p>
              </div>
              <div>
                <Label className="text-xs text-white/50 mb-1 block">Exophase Slug</Label>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-white/30 whitespace-nowrap shrink-0">exophase.com/game/</span>
                  <Input
                    value={editingGame.exophaseSlug || ""}
                    onChange={(e) => onEditingGameChange({...editingGame, exophaseSlug: e.target.value})}
                    placeholder="halo-infinite"
                    className="bg-black/50 border-white/10"
                    data-testid="exophase-slug-input"
                  />
                </div>
                <p className="text-xs text-white/30 mt-0.5">Copy from the game's Exophase URL for guaranteed match (most reliable)</p>
              </div>
            </div>
          </div>

          {/* Custom Notes (Rich Text) */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-cyan-400" />
              Custom Notes
            </Label>
            <p className="text-xs text-white/40">Rich text displayed on Library and Browse game pages. Supports bold, italic, and bullet lists.</p>
            <RichTextEditor
              value={editingGame.customNotes || ""}
              onChange={(html) => onEditingGameChange({...editingGame, customNotes: html})}
              placeholder="Add custom notes about this game..."
              className="bg-black/30"
            />
          </div>

          {/* External / Store Links */}
          <GameLinksEditor
            value={editingGame.links}
            onChange={(next) => onEditingGameChange({ ...editingGame, links: next })}
          />

          {/* Review tags — granular categorization so contributors can mark
              games as needing a specific kind of review (achievement tags,
              secrets, DLC, information, or general/other). Checking ANY box
              sets the legacy `needsReview` flag, and the list also feeds the
              new multi-select review filter on the games list. */}
          <div className={`p-3 rounded-lg border ${(editingGame.reviewTypes?.length || editingGame.needsReview) ? "bg-amber-500/10 border-amber-500/30" : "bg-white/5 border-white/10"}`}>
            <div className={`text-sm font-medium mb-2 ${(editingGame.reviewTypes?.length || editingGame.needsReview) ? "text-amber-400" : "text-white/80"}`}>
              {(editingGame.reviewTypes?.length || editingGame.needsReview) ? "Marked for Review" : "Mark for Review"}
            </div>
            <p className="text-white/50 text-xs mb-3">
              Pick the kind(s) of review this game needs. Leave all unchecked to clear the flag.
            </p>
            {(() => {
              const REVIEW_TYPE_OPTIONS = [
                { value: "tags", label: "Achievement Tag Review" },
                { value: "secrets", label: "Secrets Review" },
                { value: "dlc", label: "DLC Review" },
                { value: "info", label: "Information Review" },
                { value: "other", label: "Other / General Review" },
              ];
              const currentTypes = new Set(editingGame.reviewTypes || (editingGame.needsReview ? ["other"] : []));
              const toggleType = (value) => {
                const next = new Set(currentTypes);
                next.has(value) ? next.delete(value) : next.add(value);
                const list = Array.from(next);
                onEditingGameChange({ ...editingGame, reviewTypes: list, needsReview: list.length > 0 });
              };
              return (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {REVIEW_TYPE_OPTIONS.map(({ value, label }) => (
                    <label key={value} className="flex items-center gap-2 cursor-pointer text-sm text-white/80 hover:text-white">
                      <Checkbox
                        checked={currentTypes.has(value)}
                        onCheckedChange={() => toggleType(value)}
                        data-testid={`review-type-${value}-checkbox`}
                      />
                      {label}
                    </label>
                  ))}
                </div>
              );
            })()}
          </div>
          
          {/* Clear All Tags Button */}
          <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-red-400 text-sm font-medium">Remove All Tags & Slugs</span>
                <p className="text-white/50 text-xs">Clear all platforms, systems, and content tags</p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleClearAllTags}
                className="border-red-500/30 hover:bg-red-500/20 text-red-400"
                data-testid="clear-all-tags-btn"
              >
                <X className="w-4 h-4 mr-1" />
                Clear All
              </Button>
            </div>
          </div>
          
          {/* Published Status */}
          <div className="space-y-2" ref={publishSectionRef}>
            <Label className="flex items-center gap-2">
              <Eye className="w-4 h-4 text-green-400" />
              Visibility
            </Label>
            <label className="flex items-center gap-3 p-3 bg-black/30 rounded border border-white/10 cursor-pointer hover:bg-white/5">
              <Checkbox
                checked={editingGame.isPublished}
                onCheckedChange={(checked) => onEditingGameChange({...editingGame, isPublished: checked})}
                data-testid="is-published-checkbox"
              />
              <div>
                <span className="text-sm font-medium">Published</span>
                <p className="text-xs text-white/50">Make this game visible in Browse and Sync features</p>
              </div>
            </label>

            {/* Chatlog import — swaps the standard Sync flow for a paste-based
                Homecoming chat log importer on every user's copy of the game. */}
            <label className="flex items-center gap-3 p-3 mt-2 bg-black/30 rounded border border-white/10 cursor-pointer hover:bg-white/5">
              <Checkbox
                checked={!!editingGame.chatlogImportEnabled}
                onCheckedChange={(checked) => onEditingGameChange({...editingGame, chatlogImportEnabled: !!checked})}
                data-testid="chatlog-import-enabled-checkbox"
              />
              <div>
                <span className="text-sm font-medium">Chatlog-based sync</span>
                <p className="text-xs text-white/50">
                  Replace the standard sync menu with an <span className="text-cyan-300">Upload Chat Log</span> option
                  (currently used for City of Heroes: Homecoming).
                </p>
              </div>
            </label>
            {/* Featured — curated Browse hero row. Hard-capped server-side
                at 50; toggle rejects if that cap would be exceeded. */}
            <label className="flex items-center gap-3 p-3 mt-2 bg-black/30 rounded border border-white/10 cursor-pointer hover:bg-white/5">
              <Checkbox
                checked={!!editingGame.featured}
                onCheckedChange={(checked) => onEditingGameChange({...editingGame, featured: !!checked})}
                data-testid="featured-checkbox"
              />
              <div>
                <span className="text-sm font-medium">Featured on Browse</span>
                <p className="text-xs text-white/50">
                  Add this game to the <span className="text-amber-300">Featured Games</span> row above Recently Added (max 50 games).
                </p>
              </div>
            </label>
          </div>

          {/* Achievement-less flag — mirrors the per-user toggle in GameDetail.
              Marking this on the master entry hides the game from Library
              Maintenance for every user who adds it (the flag is mirrored into
              their user game during enrich_game_from_master_db). */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Trophy className="w-4 h-4 text-purple-400" />
              Achievements
            </Label>
            <label className="flex items-center gap-3 p-3 bg-black/30 rounded border border-white/10 cursor-pointer hover:bg-white/5">
              <Checkbox
                checked={!!editingGame.hasNoAchievements}
                onCheckedChange={(checked) => onEditingGameChange({ ...editingGame, hasNoAchievements: !!checked })}
                data-testid="mdb-has-no-achievements-checkbox"
              />
              <div>
                <span className="text-sm font-medium">No achievements / trophies</span>
                <p className="text-xs text-white/50">
                  Mark this game as having no achievements. Hides it from Library Maintenance for every user
                  and auto-applies the same flag when added to a user&apos;s library.
                </p>
              </div>
            </label>
            {/* Achievement Slop — locks the game out of all external achievement
                pipelines (Exophase, Xbox, PSN, Steam sync), disables notes/
                planner features, and excludes it from Dashboard/Statistics
                aggregations. Manual add-achievement is still allowed. */}
            <label className="flex items-start gap-3 p-3 bg-black/30 rounded border border-amber-500/30 cursor-pointer hover:bg-white/5">
              <Checkbox
                checked={!!editingGame.isAchievementSlop}
                onCheckedChange={(checked) => onEditingGameChange({ ...editingGame, isAchievementSlop: !!checked })}
                data-testid="mdb-is-achievement-slop-checkbox"
                className="mt-0.5"
              />
              <div>
                <span className="text-sm font-medium text-amber-300 flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  Achievement Slop
                </span>
                <p className="text-xs text-white/50 mt-1">
                  This game exists solely to increase achievement/trophy counts and/or provide score boosts.
                  Game includes numerous (over 500) easy to unlock (takes less than an hour) achievements.
                  Achievement data is not to be stored on this platform, and notes features and achievement
                  tracking are disabled.
                </p>
              </div>
            </label>
            {/* Unplayable — flags a game that can no longer be played (servers
                shut down, delisted, etc.) but is retained for historical and
                statistical purposes. Shows a badge on Browse pages. */}
            <label className="flex items-start gap-3 p-3 bg-black/30 rounded border border-rose-500/30 cursor-pointer hover:bg-white/5">
              <Checkbox
                checked={!!editingGame.isUnplayable}
                onCheckedChange={(checked) => onEditingGameChange({ ...editingGame, isUnplayable: !!checked })}
                data-testid="mdb-is-unplayable-checkbox"
                className="mt-0.5"
              />
              <div>
                <span className="text-sm font-medium text-rose-300 flex items-center gap-1.5">
                  <Ban className="w-3.5 h-3.5" />
                  Unplayable
                </span>
                <p className="text-xs text-white/50 mt-1">
                  Mark this game as currently unplayable (servers shut down, delisted, etc.). A badge appears on
                  Browse pages noting it is kept in the database for historical and statistical purposes for
                  those that played while it was active.
                </p>
              </div>
            </label>
          </div>
          
          {/* Linked Game Versions — group different editions/platform versions
              of the SAME game so they collapse to one Browse tile. Separate
              from cross-play (multiplayer) links above. */}
          <div className="space-y-2 p-3 bg-cyan-500/5 border border-cyan-500/20 rounded-lg">
            <Label className="flex items-center gap-2 text-cyan-300 text-sm font-medium">
              <Layers className="w-4 h-4" />
              Linked &amp; Related Games
            </Label>
            <p className="text-xs text-white/50">
              Group same-game versions (collapse to one Browse tile), and link remakes, remasters, alternate versions &amp; collections.
            </p>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={!editingGame.id}
              onClick={() => setVersionDialogOpen(true)}
              data-testid="mdb-edit-version-links"
              className="bg-white/5 border-cyan-500/30 text-cyan-300 hover:text-white hover:bg-cyan-500/10 h-8 disabled:opacity-30"
              title={!editingGame.id ? "Save the game first" : "Link versions and related games"}
            >
              <Layers className="w-3.5 h-3.5 mr-1.5" />
              Links &amp; Relations{editingGame.versionGroupId ? " (linked)" : ""}
            </Button>
          </div>

          {/* Save Button */}
          <Button onClick={onSave} data-testid="edit-game-save-btn" className="w-full bg-purple-600 hover:bg-purple-500">
            <Save className="w-4 h-4 mr-2" />
            {editingGame?.id ? "Save Changes" : "Create Game"}
          </Button>
        </div>
      </DialogContent>
      {editingGame?.id && (
        <CrossPlayLinksDialog
          open={crossPlayDialogOpen}
          onOpenChange={setCrossPlayDialogOpen}
          masterGameId={editingGame.id}
          onSaved={() => {
            // Refresh edit form's link count after admin saves
            onEditingGameChange({ ...editingGame, _crossPlayLinksUpdated: Date.now() });
          }}
        />
      )}
      {editingGame?.id && (
        <VersionLinksDialog
          open={versionDialogOpen}
          onOpenChange={setVersionDialogOpen}
          masterGameId={editingGame.id}
          masterGameName={editingGame.name}
          onSaved={(res) => {
            onEditingGameChange({ ...editingGame, versionGroupId: res?.groupId || null, _versionLinksUpdated: Date.now() });
          }}
        />
      )}
    </Dialog>
  );
}
